import {
  Web3Provider,
  InfuraProvider,
} from "@ethersproject/providers";
import WalletConnectProvider from "@walletconnect/web3-provider";
import Web3Modal from "web3modal";
// import { Bitski } from 'bitski'

import TokenContract from "./TokenContract";
import { initializeStorefront } from "./StorefrontContract";
import { initializeWETH } from "./WETHContract";
import { parseEther } from "ethers/lib/utils";
import {} from '@ethersproject/transactions'

const EIP712_DOMAIN = [
  { name: "name", type: "string" },
  { name: "version", type: "string" },
  { name: "verifyingContract", type: "address" },
  { name: "salt", type: "bytes32" },
];

const STOREFRONT_DOMAIN = {
  name: "BSC Storefront",
  version: '1'
}

const STOREFRONT_API_KEY = 'dc193ac6-4cd3-41da-9e26-e505f29bc15c'

const WETH_DOMAIN = {
  name: "Wrapped Ether",
  version: '1'
}

const WETH_API_KEY = 'e4ebfd45-290d-426a-8663-67bcc113f1b3'

const META_TRANSACTION_TYPE = [
  { name: "nonce", type: "uint256" },
  { name: "from", type: "address" },
  { name: "functionSignature", type: "bytes" }
];

export const Networks = {
  1: "mainnet",
  4: "rinkeby",
  3: "ropsten",
  5: "goerli",
  137: "matic",
  80001: "maticmum",
};

/**
 * @typedef {"mainnet" |  "rinkeby" | "ropsten" | "goerli" | "matic" | "maticmum"} NetworkName
 * @typedef {(NetworkName: NetworkName | undefined) => void} NetworkChangedHandler
 * @typedef {(account: string | null) => void} AccountChangedHandler
 * @typedef {(chainId: string | number) => void} ChainChangedHandler
 * @typedef {(code: number, reason: string) => void} DisconnectHandler
 * @typedef {import('./types/BeyondStreetCred').BeyondStreetCred} BSCTokenContract
 * @typedef {import('./types/BSCStorefront').BSCStorefront} StorefrontContract
 * @typedef {import('./types/WETH').Weth} WETHContract
 */

export class ContractManager {
  /**@type {string} */
  tokenAddress
  /**@type {string} */
  storefrontAddress
  /**@type {string} */
  maticStorefrontAddress
  /**@type {string} */
  wethAddress

  /**
   * @private
   * @type {Web3Modal}
   */
  _web3modal

  /**
   * @private
   * @type {string | null}
   */
  _account = null;
  /**
   * @private
   * @type {keyof Networks | null}
   */
  _chainId = null;
  /**
   * @private
   * @type {NetworkName}
   */
  _network;

  /**
   * @private
   * @type {string}
   */
  _infuraApiKey

  /**
   * @private
   * @type {any}
   */
  _web3Provider
  /**
   * @private
   * @type {InfuraProvider}
   */
  _defaultProvider
  /**
   * @private
   * @type {Web3Provider}
   */
  _currentProvider

  /**
   * @private
   * @type {import('./types/BSCStorefront').BSCStorefront}
   */
  _readonlyStorefront
  /**
   * @private
   * @type {import('./types/BSCStorefront').BSCStorefront}
   */
  _maticStorefront
  /**
   * @private
   * @type {import('./types/BSCStorefront').BSCStorefront}
   */
  _readWriteStorefront
  /**
   * @private
   * @type {import('./types/BeyondStreetCred').BeyondStreetCred}
   */
  _token
  /**
   * @private
   * @type {import('./types/WETH').Weth}
   */
  _weth

  /**
   * @private
   * @type {{
   *  network: NetworkChangedHandler[],
   *  accounts: AccountChangedHandler[],
   *  chain: ChainChangedHandler[],
   *  disconnect: DisconnectHandler[],
   * }} Listeners
   */
  _listeners = {
    network: [],
    accounts: [],
    chain: [],
    disconnect: [],
  };

  /**
   * @param {string} tokenAddress
   * @param {string} storefrontAddress
   * @param {string} wethAddress
   * @param {NetworkName} network
   * @param {string} infuraApiKey
   */
  constructor(
    tokenAddress,
    storefrontAddress,
    maticStorefrontAddress,
    wethAddress,
    network,
    infuraApiKey
  ) {
    this.tokenAddress = tokenAddress
    this.storefrontAddress = storefrontAddress
    this.maticStorefrontAddress = maticStorefrontAddress
    this.wethAddress = wethAddress

    this._network = network
    this._infuraApiKey = infuraApiKey
    this._defaultProvider = new InfuraProvider(this._network, this._infuraApiKey);

    this._assignContracts(this._defaultProvider)
  }

  get network() {
    return this._network;
  }

  get account() {
    return this._account;
  }

  get chainId() {
    return this._chainId;
  }

  get networkName() {
    if (!this.chainId) {
      return null;
    }

    return Networks[this.chainId];
  }

  async approveWETH() {
    const weth = this._weth
    try {
      // Do a gas estimate just to bubble up any errors
      await weth.estimateGas.approve(this.storefrontAddress, parseEther('1000'))

      /** @type{any} */
      const func = weth.interface.functions["approve(address,uint256)"]
      const functionSignature = weth.interface.encodeFunctionData(func, [this.storefrontAddress, parseEther('1000')])

      const sig = await this._signMetaTransaction(
        weth,
        WETH_DOMAIN,
        functionSignature
      )

      return await this._sendMetaTransaction(sig, functionSignature, WETH_API_KEY, this.wethAddress)
    } catch (e) {
      console.error(e)
      this._handleContractError(e)
    }
  }

  /**
   * @param {number} numberOfTokens
   */
  async buyImageToken(
    numberOfTokens
  ) {
    return this._executeBuyTx(numberOfTokens, 'buyImage(uint16)')
  }

  /**
   * @param {number} numberOfTokens
   */
  async buyVideoToken(
    numberOfTokens
  ) {
    return this._executeBuyTx(numberOfTokens, 'buyVideo(uint16)')
  }

  /**
   * @param {number} numberOfTokens
   */
  async buyCoinToken(
    numberOfTokens
  ) {
    return this._executeBuyTx(numberOfTokens, 'buyCoin(uint16)')
  }

  /**
   * @private
   * @param {number} numberOfTokens
   * @param {'buyImage(uint16)' | 'buyVideo(uint16)' | 'buyCoin(uint16)'} method
   */
  async _executeBuyTx(
    numberOfTokens,
    method
  ) {
    const storefront = this._readWriteStorefront

    if (!storefront) {
      throw new Error("Signer not connected to contract");
    }

    try {
      // Do a gas estimate just to bubble up any errors
      await storefront.estimateGas[method.substring(0, method.indexOf('('))](numberOfTokens)

      /** @type{any} */
      const func = storefront.interface.functions[method]
      const functionSignature = storefront.interface.encodeFunctionData(func, [numberOfTokens])

      const sig = await this._signMetaTransaction(
        this._readWriteStorefront,
        STOREFRONT_DOMAIN,
        functionSignature
      )

      return await this._sendMetaTransaction(sig, functionSignature, STOREFRONT_API_KEY, this.storefrontAddress)
    } catch (e) {
      console.error(e)
      this._handleContractError(e)
    }
  }

  /**
   * @private
   * @param {StorefrontContract | WETHContract} contract
   * @param {{name: string, version: string}} domain
   */
  async _signMetaTransaction(contract, domain, functionSignature) {
    if (!this._account) {
      throw new Error('Account not connected')
    }

    let domainData = {
      ...domain,
      verifyingContract: contract.address,
      salt: "0x" + this.chainId.toString(16).padStart(64, "0"),
    };

    const nonce = await contract.getNonce(this._account)

    const message = {
      nonce: nonce.toNumber(),
      from: this._account,
      functionSignature,
    }

    try {
      const sig = await this._currentProvider.getSigner()._signTypedData(
        domainData,
        {
          MetaTransaction: META_TRANSACTION_TYPE,
        },
        message
      )

      return sig
    } catch(e) {
      if (e.message !== 'Unsupported method') {
        throw e
      }

      const sig = await this._currentProvider.send(
        "eth_signTypedData_v3",
        [
          this.account,
          JSON.stringify({
            types: {
                EIP712Domain: EIP712_DOMAIN,
                MetaTransaction: META_TRANSACTION_TYPE,
            },
            domain: domainData,
            primaryType: "MetaTransaction",
            message
          })
        ]
      )

      return sig;
    }
  }

  _wait(time) {
    return new Promise(function(resolve, reject) {
      setTimeout(resolve, time);
    })
  }

  _extractSignature(signedMessage) {
    const signature = signedMessage.substring(2);
    const r = "0x" + signature.substring(0, 64);
    const s = "0x" + signature.substring(64, 128);
    const v = parseInt(signature.substring(128, 130), 16);

    return {r, s, v}
  }

  async _sendMetaTransaction(sig, functionData, apiId, toAddress) {
    const {r, s, v} = this._extractSignature(sig);
    // await contract.executeMetaTransaction(
    //   this._account, functionData, r, s, v
    // )
    const result = await fetch(`https://api.biconomy.io/api/v2/meta-tx/native`, {
      method: "POST",
      headers: {
        "x-api-key": "jn7C7tHC-.ffe65068-a706-4aaf-b48b-9a3f770eee74",
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: JSON.stringify({
        "to": toAddress,
        "apiId": apiId,
        "params": [this._account, functionData, r, s, v],
        "from": this._account,
        "signatureType": 'EIP712_SIGN'
      })
    })

    const response = await result.json()

    if (result.status === 200) {
      let tx = null

      while(tx === null) {
        tx = await this._currentProvider.getTransaction(response.txHash)

        if (!tx) {
          await this._wait(500)
        }
      }
      return tx
    } else {
      throw new Error(response.message)
    }
  }

  /** @param {string} txHash */
  async waitForTransaction(txHash) {

  }

  get readonlyStorefront() {
    return this._readonlyStorefront;
  }

  get maticStorefront() {
    return this._maticStorefront;
  }

  get token() {
    return this._token;
  }

  get weth() {
    return this._weth;
  }

  async connectWallet() {
    const provider = await this.web3Modal.connect();

    provider.on("chainChanged", this._providerChainChanged);
    provider.on("accountsChanged", this._providerAccountChanged);
    provider.on("disconnect", this._providerDisconnect);

    this._web3Provider = provider;

    this._updateProvider();
  }

  /**
   * @param {AccountChangedHandler} callback
   */
  onAccountsChanged(callback) {
    this._listeners.accounts.push(callback);
  }

  /**
   * @param {AccountChangedHandler} callback
   */
  offAccountsChanged(callback) {
    this._listeners.accounts.splice(
      this._listeners.accounts.indexOf(callback),
      1
    );
  }

  /**
   * @param {AccountChangedHandler} callback
   */
  onChainChanged(callback) {
    this._listeners.chain.push(callback);
  }

  /**
   * @param {ChainChangedHandler} callback
   */
  offChainChanged(callback) {
    this._listeners.chain.splice(this._listeners.chain.indexOf(callback), 1);
  }

  /**
   * @param {NetworkChangedHandler} callback
   */
  onNetworkChanged(callback) {
    this._listeners.network.push(callback);
  }

  /**
   * @param {NetworkChangedHandler} callback
   */
  offNetworkChanged(callback) {
    this._listeners.network.splice(
      this._listeners.network.indexOf(callback),
      1
    );
  }

  /**
   * @param {DisconnectHandler} callback
   */
  onDisconnect(callback) {
    this._listeners.disconnect.push(callback);
  }

  /**
   * @param {DisconnectHandler} callback
   */
  offDisconnect(callback) {
    this._listeners.disconnect.splice(
      this._listeners.disconnect.indexOf(callback),
      1
    );
  }

  /**@private */
  _calculateGas(gasEstimate) {
    // add 30% of gas estimate to the estimate for 130% total
    return gasEstimate.div(10).mul(3).add(gasEstimate);
  }

  /**@private */
  _handleContractError(e) {
    switch (e.code) {
      case 4001:
        throw new Error("User rejected transaction");
      case "UNPREDICTABLE_GAS_LIMIT":
        throw new Error(e.error.message);
      default:
        if (e.message.match(/insufficient funds/)) {
          throw new Error("Insufficient funds")
        }
        if (e.data?.message?.match(/execution reverted/)) {
          throw new Error(e.data.message.substring(20))
        }
        throw new Error(e.message);
    }
  }

  get web3Modal() {
    if (this._web3modal) {
      return this._web3modal;
    }

    // const bitskiNetwork = this.network == 'maticmum' ? 'mumbai' : 'polygon'

    this._web3modal = new Web3Modal({
      network: this.network,
      cacheProvider: true,
      providerOptions: {
        walletconnect: {
          package: WalletConnectProvider,
          options: {
            infuraId: this._infuraApiKey,
          },
        },
        // bitski: {
        //   package: Bitski,
        //   options: {
        //     clientId: "d18877c2-4869-472a-825a-c78c9e13537d",
        //     callbackUrl: `${window.location.origin}/bitski-callback.html`,
        //     extraProviderOptions: {
        //       networkName: bitskiNetwork,
        //     },
        //   },
        // },
      },
    });

    return this._web3modal;
  }

  /**@private */
  async _updateProvider() {
    const ethersProvider = new Web3Provider(this._web3Provider);

    this._assignProvider(ethersProvider);

    const signer = ethersProvider.getSigner();

    const account = await signer.getAddress();
    const chainId = await signer.getChainId();

    this._updateAccount(account);
    this._updateChain(chainId);
  }

  /**
   * @private
   * @param {Web3Provider} provider
   */
  _assignProvider(provider) {
    this._currentProvider = provider;

    if (provider) {
      this._assignContracts(provider)
    } else {
      this._assignContracts(this._defaultProvider)
    }
  }

  /**@private */
  _updateAccount(account) {
    this._account = account;

    this._listeners.accounts.forEach((fn) => {
      fn(account);
    });
  }

  /**@private */
  _updateChain(chainId) {
    if (typeof chainId === "string") {
      chainId = parseInt(chainId, 16);
    }

    this._chainId = chainId;

    if (Networks[this._chainId] !== this.network) {
      alert(`You must be connected to the ${this.network} network.`);
      window.location.reload();
    }

    this._listeners.chain.forEach((fn) => {
      fn(chainId);
    });

    this._listeners.network.forEach((fn) => {
      fn(this._network);
    });
  }

  /**@private */
  _disconnect(code, reason) {
    this._account = null;
    this._chainId = null;

    this._listeners.disconnect.forEach((fn) => {
      fn(code, reason);
    });
  }

  /**@private */
  _providerAccountChanged = () => {
    this._updateProvider();
  };

  /**@private */
  _providerChainChanged = () => {
    this._updateProvider();
  };

  /**@private */
  _providerDisconnect(code, reason) {
    this._disconnect(code, reason);
  }

  /**@private */
  _assignContracts(provider) {
    this._readonlyStorefront = initializeStorefront(this.storefrontAddress, provider)
    this._maticStorefront = initializeStorefront(this.maticStorefrontAddress, provider)

    this._token = new TokenContract(
      this.tokenAddress,
      provider
    );

    if (this._currentProvider) {
      this._readWriteStorefront = initializeStorefront(this.storefrontAddress, this._currentProvider.getSigner())
      this._weth = initializeWETH(this.wethAddress, this._currentProvider.getSigner())
    }
  }
}
